import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LoginPage from "@/pages/LoginPage";
import CrewLoginPage from "@/pages/CrewLoginPage";
import DispatcherConsole from "@/pages/DispatcherConsole";
import AmbulanceConsole from "@/pages/AmbulanceConsole";
import AdminPanel from "@/pages/AdminPanel";
import FireMDTPage from "@/pages/FireMDTPage";
import TrackerPage from "@/pages/TrackerPage";

function App() {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem("dispatch_user");
    return saved ? JSON.parse(saved) : null;
  });

  // Iter 98 — Demo Mode: whenever the logged-in user has `demo_mode: true`
  // (set at login), attach `X-Demo-Mode: true` to every axios request so
  // the backend routes to demo_* collections. Kept in sync with `user`.
  useEffect(() => {
    if (user && user.demo_mode) {
      axios.defaults.headers.common["X-Demo-Mode"] = "true";
    } else {
      delete axios.defaults.headers.common["X-Demo-Mode"];
    }
  }, [user]);

  // Register the Stadia tile-cache service worker on first mount. It intercepts
  // every request to tiles.stadiamaps.com and serves from an on-disk cache
  // (cache-first, 30-day TTL, ~60 MB LRU). Saves Stadia API credits on repeat
  // pan/zoom of the same areas.
  useEffect(() => {
    if ("serviceWorker" in navigator && window.location.protocol === "https:") {
      navigator.serviceWorker.register("/tile-sw.js").catch((err) => {
        console.warn("Tile cache SW registration failed:", err);
      });
    }
  }, []);

  useEffect(() => {
    if (user) {
      localStorage.setItem("dispatch_user", JSON.stringify(user));
    } else {
      localStorage.removeItem("dispatch_user");
    }
  }, [user]);

  // Iter 109 — Multi-tab logout coordination. When a dispatcher signs out
  // in one browser tab, every other tab that shares the same localStorage
  // (same origin) also drops the session. Uses the native `storage` event
  // which fires in ALL OTHER tabs whenever localStorage is mutated.
  useEffect(() => {
    const onStorage = (e) => {
      // Only react to changes to the dispatch user key. Ignore anything
      // else so unrelated writes (e.g., MDT terminal-uuid, tile cache)
      // don't kick us out.
      if (e.key !== "dispatch_user") return;
      // newValue is null on removeItem() or a JSON string on setItem().
      if (!e.newValue) {
        setUser(null);
      } else {
        try { setUser(JSON.parse(e.newValue)); } catch { /* ignore */ }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    setUser(null);
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Landing page */}
          <Route path="/" element={<LandingPage />} />
          {/* Dispatcher login — supports sub-routes so dispatchers can right-click
              a tab and "Open in New Tab". Each new tab loads an independent
              WebSocket connection and receives the same broadcasts. */}
          <Route
            path="/dispatcher/*"
            element={
              user && user.role === "dispatcher" ? (
                <DispatcherConsole user={user} onLogout={handleLogout} />
              ) : (
                <LoginPage onLogin={handleLogin} />
              )
            }
          />
          {/* Ambulance Crew login - direct entry */}
          <Route
            path="/crew"
            element={<CrewLoginPage onLogin={handleLogin} />}
          />
          {/* Ambulance Console */}
          <Route
            path="/ambulance"
            element={
              user && user.role === "crew" ? (
                <AmbulanceConsole user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/crew" replace />
              )
            }
          />
          {/* Admin Panel — requires a logged-in dispatcher. Permission-aware
              so a plain DISPATCHER sees only read-only views (Unit History),
              while AGENCY ADMIN / SUPERVISOR / the built-in admin can
              add/edit Users, Agencies, Hospitals, Call Types and KMLs. */}
          <Route
            path="/admin"
            element={
              user && user.role === "dispatcher" ? (
                <AdminPanel user={user} />
              ) : (
                <LoginPage onLogin={handleLogin} />
              )
            }
          />
          {/* Fire MDT — IP-bound, no manual login. The bind selector
              shows the first time a Fire-house terminal hits this URL.
              Once bound, every reload auto-identifies the apparatus via
              source IP. */}
          <Route path="/fire/mdt" element={<FireMDTPage />} />
          {/* Standalone GPS tracker for tablets / laptops (token-linked to a unit). */}
          <Route path="/tracker" element={<TrackerPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

function LandingPage() {
  return (
    <div style={{ background: "#000", color: "#FFF", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'VT323', monospace" }}>
      <div style={{ textAlign: "center" }}>
        <h1 style={{ fontSize: 36, color: "#00BFFF", letterSpacing: 3, marginBottom: 8 }}>NYC VOLUNTEER EMS/FIRE COMMUNICATION</h1>
        <h2 style={{ fontSize: 18, color: "#888", marginBottom: 48, letterSpacing: 2 }}>DISPATCH SYSTEM</h2>
        <div style={{ display: "flex", gap: 32, justifyContent: "center" }}>
          <a href="/dispatcher" data-testid="link-dispatcher" style={{
            display: "block", padding: "24px 48px", border: "2px solid #00BFFF", borderRadius: 4,
            color: "#00BFFF", fontSize: 22, textDecoration: "none", letterSpacing: 2,
            background: "rgba(0,191,255,0.05)", transition: "background 0.2s",
          }}>
            DISPATCHER<br/><span style={{ fontSize: 14, color: "#666" }}>Console</span>
          </a>
          <a href="/crew" data-testid="link-crew" style={{
            display: "block", padding: "24px 48px", border: "2px solid #00FF00", borderRadius: 4,
            color: "#00FF00", fontSize: 22, textDecoration: "none", letterSpacing: 2,
            background: "rgba(0,255,0,0.05)", transition: "background 0.2s",
          }}>
            AMBULANCE<br/><span style={{ fontSize: 14, color: "#666" }}>Crew MDT</span>
          </a>
          <a href="/fire/mdt" data-testid="link-fire-mdt" style={{
            display: "block", padding: "24px 48px", border: "2px solid #FF7030", borderRadius: 4,
            color: "#FF7030", fontSize: 22, textDecoration: "none", letterSpacing: 2,
            background: "rgba(255,112,48,0.05)", transition: "background 0.2s",
          }}>
            FIRE<br/><span style={{ fontSize: 14, color: "#666" }}>Apparatus MDT</span>
          </a>
        </div>
      </div>
    </div>
  );
}

export default App;
